import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, useAuthGuard } from '../../contexts/AuthContext';
import { peekReturnTo } from '../../utils/returnTo';
import { tokenService } from '../../services/tokenService';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

/**
 * AuthGuard component that protects routes requiring authentication
 * Redirects to auth page if user is not authenticated fabric buyer
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireAuth = true,
  redirectTo = '/auth'
}) => {
  const { isAuthorizedFabricBuyer, shouldRedirectToAuth, loading } = useAuthGuard();
  const { profileState } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If auth is required and user should be redirected
  if (requireAuth && shouldRedirectToAuth) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  const isGoogleCallback = location.pathname === '/auth/google/callback';

  // Redirect authenticated users based on profile completion status
  if (isAuthorizedFabricBuyer && !profileState.loading) {
    if (!requireAuth && isGoogleCallback) {
      return <>{children}</>;
    }
    const pc = profileState?.profileCompletion;
    if (!pc) {
      try {
        const cached = JSON.parse(localStorage.getItem('fb_profile_cache') || 'null');
        if (cached?.profileCompletion?.isComplete === false && location.pathname !== '/profile-completion') {
          return <Navigate to="/profile-completion" replace />;
        }
      } catch {}
    }
    if (pc) {
      // If profile incomplete and not already on completion route, redirect there
      if (!pc.isComplete && location.pathname !== '/profile-completion') {
        return <Navigate to="/profile-completion" replace />;
      }
      // If profile is complete and user hits a public route, redirect to dashboard
      if (!requireAuth && pc.isComplete && location.pathname !== '/dashboard') {
        return <Navigate to="/dashboard" replace />;
      }
      // If profile is complete but user somehow hits completion route, send to dashboard
      if (pc.isComplete && location.pathname === '/profile-completion') {
        if (typeof window !== 'undefined') {
          console.trace('[dashboard-redirect] AuthGuard profile complete on /profile-completion', {
            pathname: window.location.pathname,
            returnTo: peekReturnTo(),
            profileCompletion: pc
          });
        }
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  return <>{children}</>;
};

/**
 * Public route guard - redirects authenticated users to dashboard
 */
export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <AuthGuard requireAuth={false}>{children}</AuthGuard>;
};

/**
 * Protected route guard - requires fabric buyer authentication
 */
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <AuthGuard requireAuth={true}>{children}</AuthGuard>;
}; 
